import React, { useState } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, Button, Modal, Space, Typography, Input, Select, Tag, Empty, Spin, Switch, Divider, message } from "antd";
import { SaveOutlined, EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, LinkOutlined, RobotOutlined, ThunderboltOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { User } from "../../../../types/admin/UserTypes";
import { PatientNote } from "../../../../types/healthcare/PatientNotes";
import { can } from "../../../../utils/userPrefs/validatePermissions";
import { getAllRolesPractitioner } from "../../../../api/admin/rolesApi";
import { Role } from "../../../../types/admin/RoleTypes";
import { useAuthSession } from "../../../../lib/http/session";
import { fetchPatientNotes, createPatientNote, updatePatientNote, deletePatientNote, UpdatePatientNotePayload } from "../../../../api/healthcare/PatientNotesApi";
import { generateScratchpadDraft, confirmScratchpad, ScratchpadDraftResponse } from "../../../../api/healthcare/ScratchpadApi";
import { updatePatient } from "../../../../api/healthcare/PatientApi";
import { stripHtml, canEditNote, noteTags, roleRanks } from "./notesUtils";
import { SCRATCHPAD_SECTIONS, sectionLabel, tabForSection, buildConfirmPayload } from "./scratchpadUtils";
import ScratchpadDraftForm from "./submodals/ScratchpadDraftForm";

const { Text } = Typography;
const { confirm } = Modal;

/** Suggested categories for the composer + list filter (free text also allowed on the composer). */
const CATEGORY_OPTIONS = ["General", "Clinical", "Administrative", "Follow-up", "Medication", "Lab"].map((c) => ({ label: c, value: c }));

interface NotesProps {
	patient: Patient;
	// Optional: switch the parent PatientModal to a tab when a linked item is clicked (deep-link).
	onNavigateToSection?: (tabKey: string) => void;
	// Legacy props (pre-healthcare migration). Notes now manages its own data via React Query, so
	// these are accepted but unused — kept so existing call sites keep type-checking.
	notes?: PatientNote[];
	setNotes?: React.Dispatch<React.SetStateAction<PatientNote[]>>;
	handleAutoSave?: () => void;
}

const Notes: React.FC<NotesProps> = ({ patient, onNavigateToSection }) => {
	const user: User = getSessionUser();
	const sessionRoles = useAuthSession().info?.roles ?? [];
	// healthcare authorises edits from the verified caller's snapshot ranks; these ranks only decide which buttons show.
	const { data: snapshotRoles } = useQuery<Role[]>({ queryKey: ["practitionerRoles"], queryFn: getAllRolesPractitioner, staleTime: 5 * 60_000 });
	const ranks = snapshotRoles ? roleRanks(snapshotRoles) : undefined;
	const patientId = patient.id;
	const queryClient = useQueryClient();

	// Filters
	const [search, setSearch] = useState<string>("");
	const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
	const [tagFilter, setTagFilter] = useState<string | undefined>(undefined);

	// Composer
	const [newNote, setNewNote] = useState<string>("");
	const [newCategory, setNewCategory] = useState<string | undefined>(undefined);
	const [newTags, setNewTags] = useState<string[]>([]);

	// AI scratchpad (single-shot: write → one draft → review/edit → confirm)
	const [aiMode, setAiMode] = useState<boolean>(false);
	const [aiSection, setAiSection] = useState<string>("");
	const [draft, setDraft] = useState<ScratchpadDraftResponse | null>(null);
	const [draftFields, setDraftFields] = useState<Record<string, any>>({});

	// Inline edit
	const [editingNoteId, setEditingNoteId] = useState<number | string | null>(null);
	const [editingText, setEditingText] = useState<string>("");
	const [editingCategory, setEditingCategory] = useState<string | undefined>(undefined);
	const [editingTags, setEditingTags] = useState<string[]>([]);

	const { data: notes = [], isLoading } = useQuery({
		queryKey: ["patientNotes", patientId, search, categoryFilter, tagFilter],
		queryFn: () =>
			fetchPatientNotes(patientId, {
				q: search || undefined,
				category: categoryFilter,
				tag: tagFilter,
			}),
		enabled: !!patientId,
	});

	const invalidate = () => queryClient.invalidateQueries({ queryKey: ["patientNotes", patientId] });

	const createMutation = useMutation({
		mutationFn: createPatientNote,
		onSuccess: () => {
			invalidate();
			setNewNote("");
			setNewCategory(undefined);
			setNewTags([]);
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, payload }: { id: number | string; payload: UpdatePatientNotePayload }) => updatePatientNote(id, payload),
		onSuccess: () => {
			invalidate();
			setEditingNoteId(null);
			setEditingText("");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number | string) => deletePatientNote(id, { actor_id: user.id }),
		onSuccess: invalidate,
	});

	const resetAi = () => {
		setDraft(null);
		setDraftFields({});
		setNewNote("");
		setNewCategory(undefined);
		setNewTags([]);
		setAiSection("");
	};

	// Single-shot generation: note text → one structured draft (no conversation history).
	const generateMutation = useMutation({
		mutationFn: () => generateScratchpadDraft(stripHtml(newNote), aiSection || undefined),
		onSuccess: (d) => {
			setDraft(d);
			setDraftFields(d.fields || {});
		},
		onError: () => message.error("Could not generate a draft. Try again."),
	});

	// Confirm: create the AI note, persist the profile item + link the note, applying the
	// personal-info patch through the existing auth updatePatient() path when required.
	const confirmMutation = useMutation({
		mutationFn: async () => {
			const section = draft?.section || aiSection;
			if (!section) throw new Error("No section");

			const createdNote = await createPatientNote({
				patient_id: patientId,
				author_id: user.id,
				body: newNote,
				category: newCategory ?? null,
				tags: newTags.length ? newTags : null,
				is_ai_assisted: true,
				ai_status: "draft",
			});

			const response = await confirmScratchpad(
				createdNote.id,
				buildConfirmPayload({ section, draft: draftFields, body: newNote, actorId: user.id }),
			);

			if (response.result.apply_via === "auth_update" && response.result.patch) {
				await updatePatient(patientId, response.result.patch);
			}

			return response;
		},
		onSuccess: () => {
			message.success("Saved — profile item created and linked to the note.");
			resetAi();
			invalidate();
		},
		onError: () => message.error("Could not save the item. Check the draft and try again."),
	});

	const detectedSection = draft?.section || aiSection;
	const canConfirm = !!draft && !!detectedSection && draft.supported !== false;

	const handleSaveClick = () => {
		if (!stripHtml(newNote)) return;
		createMutation.mutate({
			patient_id: patientId,
			author_id: user.id,
			body: newNote,
			category: newCategory ?? null,
			tags: newTags.length ? newTags : null,
		});
	};

	const handleEditClick = (note: PatientNote) => {
		setEditingNoteId(note.id);
		setEditingText(note.body || "");
		setEditingCategory(note.category ?? undefined);
		// `noteTags`, not `note.tags ?? []` — a malformed row must not reach the tags Select as a
		// non-array value. See notesUtils.noteTags.
		setEditingTags(noteTags(note));
	};

	const handleEditSave = (note: PatientNote) => {
		if (!stripHtml(editingText)) return;
		updateMutation.mutate({
			id: note.id,
			payload: {
				actor_id: user.id,
				body: editingText,
				category: editingCategory ?? null,
				tags: editingTags,
			},
		});
	};

	const handleEditCancel = () => {
		setEditingNoteId(null);
		setEditingText("");
	};

	const handleDeleteClick = (note: PatientNote) => {
		confirm({
			title: "Delete Note",
			content: "Are you sure you want to delete this note? This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => deleteMutation.mutate(note.id),
		});
	};

	const canEditOrDelete = (note: PatientNote): boolean => can("healthcare.notes.write") && canEditNote(note, user.id, sessionRoles, ranks);

	// Tag options for the filter — derived from the loaded notes.
	// ⚠️ `flatMap` only flattens ARRAYS, so a non-array `tags` would arrive here as a single
	// element and render as an option label — "Objects are not valid as a React child".
	const tagOptions = Array.from(new Set(notes.flatMap((n) => noteTags(n)))).map((t) => ({ label: t, value: t }));

	const quillModules = { toolbar: false };
	const quillFormats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "indent", "link"];

	return (
		<div style={{ padding: "16px", height: "100%", overflowY: "auto" }}>
			<style>{`
				.ql-editor { min-height: 100px; border: 1px solid #d9d9d9; border-radius: 6px; }
				.ql-container { border: none; }
			`}</style>

			{/* Composer */}
			<Card style={{ marginBottom: 8 }}>
				<Space style={{ marginBottom: 8 }}>
					<Switch
						checked={aiMode}
						onChange={(checked) => {
							setAiMode(checked);
							setDraft(null);
						}}
						checkedChildren={<ThunderboltOutlined />}
					/>
					<Text>AI assisted</Text>
				</Space>

				<ReactQuill value={newNote} onChange={setNewNote} modules={quillModules} formats={quillFormats} theme="snow" placeholder={aiMode ? "Describe what happened — e.g. 'patient had hip cam surgery in 2017'" : "Write your note here..."} />

				{aiMode ? (
					<Space style={{ marginTop: 12, width: "100%" }} direction="vertical">
						<Select placeholder="Section (auto-detect)" style={{ width: "100%" }} value={aiSection || undefined} onChange={(v) => setAiSection(v ?? "")} options={SCRATCHPAD_SECTIONS} allowClear />

						{!draft ? (
							<Button type="primary" icon={<ThunderboltOutlined />} onClick={() => generateMutation.mutate()} loading={generateMutation.isPending} disabled={!stripHtml(newNote)}>
								Generate draft
							</Button>
						) : (
							<div data-testid="scratchpad-draft">
								<Divider plain>
									Draft · {sectionLabel(detectedSection)}
									{typeof draft.confidence === "number" && draft.confidence > 0 ? ` · ${Math.round(draft.confidence * 100)}% confidence` : ""}
								</Divider>

								{!draft.supported && detectedSection && (
									<Text type="warning" style={{ display: "block", marginBottom: 8 }}>
										This section can't be auto-created yet — pick another section or save as a plain note.
									</Text>
								)}

								<ScratchpadDraftForm section={detectedSection} value={draftFields} onChange={setDraftFields} />

								<Space style={{ marginTop: 12 }} wrap>
									<Button type="primary" icon={<CheckOutlined />} onClick={() => confirmMutation.mutate()} loading={confirmMutation.isPending} disabled={!canConfirm}>
										Confirm &amp; Save
									</Button>
									<Button icon={<ThunderboltOutlined />} onClick={() => generateMutation.mutate()} loading={generateMutation.isPending}>
										Regenerate
									</Button>
									<Button icon={<CloseOutlined />} onClick={() => setDraft(null)}>
										Cancel
									</Button>
								</Space>
							</div>
						)}
					</Space>
				) : (
					<Space style={{ marginTop: 12, width: "100%" }} direction="vertical">
						<Select allowClear showSearch placeholder="Category" style={{ width: "100%" }} value={newCategory} onChange={(v) => setNewCategory(v)} options={CATEGORY_OPTIONS} />
						<Select mode="tags" allowClear placeholder="Add tags" style={{ width: "100%" }} value={newTags} onChange={(v) => setNewTags(v)} />
						<Button type="primary" icon={<SaveOutlined />} onClick={handleSaveClick} loading={createMutation.isPending} disabled={!stripHtml(newNote)}>
							Save Note
						</Button>
					</Space>
				)}
			</Card>

			{/* Filters */}
			<Space style={{ marginBottom: 16, width: "100%" }} direction="vertical">
				<Input.Search placeholder="Search notes…" allowClear value={search} onChange={(e) => setSearch(e.target.value)} />
				<Space style={{ width: "100%" }}>
					<Select allowClear placeholder="Category" style={{ minWidth: 140 }} value={categoryFilter} onChange={(v) => setCategoryFilter(v)} options={CATEGORY_OPTIONS} />
					<Select allowClear placeholder="Tag" style={{ minWidth: 140 }} value={tagFilter} onChange={(v) => setTagFilter(v)} options={tagOptions} />
				</Space>
			</Space>

			{/* List */}
			{isLoading ? (
				<div style={{ textAlign: "center", padding: 24 }}>
					<Spin />
				</div>
			) : notes.length === 0 ? (
				<Empty description="No notes" />
			) : (
				notes.map((note) => (
					<Card key={note.id} size="small" style={{ marginBottom: 16 }}>
						{editingNoteId === note.id ? (
							<div>
								<ReactQuill value={editingText} onChange={setEditingText} modules={quillModules} formats={quillFormats} theme="snow" placeholder="Edit your note..." />
								<Select allowClear showSearch placeholder="Category" style={{ width: "100%", marginTop: 8 }} value={editingCategory} onChange={(v) => setEditingCategory(v)} options={CATEGORY_OPTIONS} />
								<Select mode="tags" allowClear placeholder="Add tags" style={{ width: "100%", marginTop: 8 }} value={editingTags} onChange={(v) => setEditingTags(v)} />
								<Space style={{ marginTop: 12 }}>
									<Button type="primary" icon={<CheckOutlined />} size="small" onClick={() => handleEditSave(note)} loading={updateMutation.isPending} disabled={!stripHtml(editingText)}>
										Save
									</Button>
									<Button icon={<CloseOutlined />} size="small" onClick={handleEditCancel}>
										Cancel
									</Button>
								</Space>
							</div>
						) : (
							<div>
								<Space size={[4, 4]} wrap style={{ marginBottom: 8 }}>
									{note.category && <Tag color="blue">{note.category}</Tag>}
									{noteTags(note).map((t) => (
										<Tag key={t}>{t}</Tag>
									))}
									{note.is_ai_assisted && (
										<Tag icon={<RobotOutlined />} color="purple">
											AI
										</Tag>
									)}
									{note.linked_item_type && (
										<Tag
											icon={<LinkOutlined />}
											color="green"
											style={{ cursor: onNavigateToSection && tabForSection(note.linked_item_type) ? "pointer" : "default" }}
											onClick={() => {
												const tab = tabForSection(note.linked_item_type);
												if (tab && onNavigateToSection) onNavigateToSection(tab);
											}}
										>
											{sectionLabel(note.linked_item_type)}
										</Tag>
									)}
								</Space>
								<div dangerouslySetInnerHTML={{ __html: note.body || "" }} />
								<div>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{note.created_at ? new Date(note.created_at).toLocaleString() : ""}
									</Text>
								</div>
								{canEditOrDelete(note) && (
									<Space style={{ marginTop: 12 }}>
										<Button type="text" icon={<EditOutlined />} size="small" onClick={() => handleEditClick(note)}>
											Edit
										</Button>
										<Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={() => handleDeleteClick(note)} loading={deleteMutation.isPending}>
											Delete
										</Button>
									</Space>
								)}
							</div>
						)}
					</Card>
				))
			)}
		</div>
	);
};

export default Notes;
